<template>
  <nav
    :class="{ active: navIsActive }"
    aria-label="Secondary"
    class="mobile-secondary"
    role="navigation"
  >
    <TransitionGroup appear name="slide-up">
      <div v-if="navIsActive" key="secondary-close" class="secondary-close">
        <div v-if="navHistory.length" class="button back" @click="goBack">
          <Icon name="fa6-solid:chevron-left" />
        </div>
        <div class="button close" @click="closeNav">
          <Icon name="fa6-solid:xmark" />
        </div>
      </div>
      <div v-if="navIsActive" key="secondary-tabs" class="secondary-tabs">
        <NavigationSecondaryTabMenu />
      </div>
    </TransitionGroup>
    <div class="secondary-buttons flex justify-around align-center">
      <NavigationSecondaryButton label="Menu" @click="setActiveMenu('Menu')">
        <Icon name="fa6-solid:bars" />
      </NavigationSecondaryButton>
      <NavigationSecondaryButton
        label="Topics"
        @click="setActiveMenu('Topics')"
      >
        <Icon name="fa6-regular:bookmark" />
      </NavigationSecondaryButton>
      <NavigationSecondaryButton
        label="Find Help"
        @click="setActiveMenu('Find Help')"
      >
        <Icon name="fa6-solid:life-ring" />
      </NavigationSecondaryButton>
      <NavigationSecondaryButton
        label="Search"
        @click="setActiveMenu('Search')"
      >
        <Icon name="fa6-solid:magnifying-glass" />
      </NavigationSecondaryButton>
    </div>
  </nav>
</template>

<script setup lang="ts">
import type { NavigationMenuItemStoryblok } from '~/types/storyblok'

const route = useRoute()
const mobileNavStore = useMobileNavStore()
const { openNav, closeNav, goBack, showMobileNav, showMobileNavChild } =
  mobileNavStore
const { navIsActive, navHistory } = storeToRefs(mobileNavStore)
const { navigationMenu } = await useConfigStore()
const headerMenu = computed(() => navigationMenu?.header)
const mobileMenu = computed(
  () =>
    [
      ...(headerMenu.value || []),
      {
        _uid: 'mobileHeaderMenu',
        component: 'NavigationMenuMobileItem',
        label: 'Search',
        children: [{ component: 'NavigationSecondaryTabSearch' }],
        link: {
          cached_url: 'search/',
          linktype: 'story',
        },
      },
    ] as NavigationMenuItemStoryblok[],
)

function setActiveMenu(label) {
  /**
   * Check if label matches item in top-level nav.
   *
   * If so, add top-level nav menu to nav history and then navigate to the matched nav item menu.
   * Otherwise, just navigate to top-level nav menu.
   */
  const menu = mobileMenu.value.find((navItem) => {
    return navItem?.label === label
  })

  if (!menu) {
    showMobileNav({ menu: mobileMenu.value, label: 'Menu' })
  } else {
    showMobileNavChild({ menu: menu.children, label })
  }
  openNav()
}

watch(
  () => route.fullPath,
  () => {
    closeNav()
  },
)
</script>

<style lang="less" scoped>
.mobile-secondary {
  @media print {
    .hidden();
  }

  @media @wide {
    .hidden();
  }

  .secondary-close {
    .fixed();
    .flex();

    top: 0;
    z-index: 10002;
    height: @72px;
    width: 100%;

    .button {
      .absolute();
      .content-box();
      .justify-center();

      width: @21px;
      height: @11px;
      top: @gutter-xs;
      padding: @gutter @gutter-xs;
      border: 0;
      background: transparent;
      color: @color-fill-less-muted;

      &.back {
        left: @gutter-xs + @2px;
      }

      &.close {
        right: @gutter-xs + @2px;
      }
    }
  }

  .secondary-buttons,
  .secondary-tabs {
    .fixed();
    .flex();

    padding: 0;
    bottom: 0;
    width: 100%;
    background: @color-fill-alternate;
  }

  .secondary-buttons {
    z-index: 9996;
    border-top: solid @1px @color-border;
  }

  .secondary-tabs {
    .flex-col();

    top: 0;
    z-index: 10001;
    height: 100%;
    padding: @72px 0 0;
  }
}

// Transitions
.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s cubic-bezier(0.4, 0.6, 0.2, 1);
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
